import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { Tag } from "../Tag";
import type { SegmentProps } from "./types";

export const Segment = ({
  items,
  activeItem,
  onChange,
  className,
  variant = "hug",
}: SegmentProps) => {
  const { rtl } = useTranslation();

  const selectionRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const activeIndex = useMemo(() => {
    if (!activeItem) return -1;
    return items.findIndex((item) => item.id === activeItem);
  }, [items, activeItem]);

  const getActiveButton = useCallback((): HTMLButtonElement | null => {
    if (activeIndex < 0) return null;
    const id = items[activeIndex]?.id;
    if (!id) return null;
    return buttonRefs.current.get(id) ?? null;
  }, [activeIndex, items]);

  const setSelection = useCallback(() => {
    const selection = selectionRef.current;
    const button = getActiveButton();
    if (!selection || !button) return;

    const buttonRect = button.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();

    if (!containerRect) return;

    const width = buttonRect.width;
    const left = buttonRect.left - containerRect.left;

    selection.style.left = `${left}px`;
    selection.style.width = `${width}px`;

    if (!selection.hasAttribute("data-initialized")) {
      selection.setAttribute("data-initialized", "true");
    }
  }, [getActiveButton]);

  useLayoutEffect(() => {
    setTimeout(setSelection);
  }, [activeIndex, items, setSelection]);

  useEffect(() => {
    const container = containerRef.current;
    const button = getActiveButton();
    if (!container) return;

    const ro = new ResizeObserver(() => {
      setSelection();
    });

    ro.observe(container);
    if (button) ro.observe(button);

    return () => {
      ro.disconnect();
    };
  }, [getActiveButton, setSelection]);

  const handleButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const nextId = event.currentTarget.getAttribute("data-segment-id");
      const isDisabled =
        event.currentTarget.getAttribute("aria-disabled") === "true";
      if (!nextId || isDisabled) return;
      onChange(nextId);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const enabled = items.filter((i) => !i.disabled);
      if (enabled.length === 0) return;

      const focused = document.activeElement as HTMLElement | null;
      const focusedId = focused?.getAttribute?.("data-segment-id");
      if (!focusedId) return;

      const currentIndex = enabled.findIndex((i) => i.id === focusedId);
      if (currentIndex < 0) return;

      const moveFocus = (nextIndex: number) => {
        const nextItem = enabled[nextIndex];
        if (!nextItem) return;
        const btn = buttonRefs.current.get(nextItem.id);
        btn?.focus();
      };

      switch (event.key) {
        case "ArrowLeft": {
          event.preventDefault();
          const next = rtl
            ? (currentIndex + 1) % enabled.length
            : (currentIndex - 1 + enabled.length) % enabled.length;
          moveFocus(next);
          break;
        }
        case "ArrowRight": {
          event.preventDefault();
          const next = rtl
            ? (currentIndex - 1 + enabled.length) % enabled.length
            : (currentIndex + 1) % enabled.length;
          moveFocus(next);
          break;
        }
        case "Home":
          event.preventDefault();
          moveFocus(0);
          break;
        case "End":
          event.preventDefault();
          moveFocus(enabled.length - 1);
          break;
        case "Enter":
        case " ": {
          event.preventDefault();
          const btn = buttonRefs.current.get(focusedId);
          const nextId = btn?.getAttribute("data-segment-id");
          const isDisabled = btn?.getAttribute("aria-disabled") === "true";
          if (nextId && !isDisabled) onChange(nextId);
          break;
        }
        default:
          break;
      }
    },
    [items, onChange, rtl],
  );

  const itemElements = useMemo(() => {
    const baseButton = cn(
      "relative z-10 h-full rounded-[0.625rem] font-medium text-sm text-center transition-colors duration-200 whitespace-nowrap px-3 flex items-center justify-center gap-1",
      "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-[-2px]",
      "motion-reduce:transition-none",
      variant === "fill" ? "flex-1" : "w-full",
    );

    return items.map((item) => {
      const isActive = item.id === activeItem;
      const btnClass = cn(
        baseButton,
        "text-neutral-500",
        !item.disabled &&
          !isActive &&
          "hover:bg-neutral-500/5 focus-visible:bg-neutral-500/5 active:bg-neutral-500/10",
        item.disabled && "text-neutral-300 cursor-not-allowed opacity-50",
        item.className,
      );

      return (
        <button
          key={item.id}
          {...(item.dataAttributes || {})}
          type="button"
          ref={(el) => {
            if (el) buttonRefs.current.set(item.id, el);
            else buttonRefs.current.delete(item.id);
          }}
          className={btnClass}
          data-segment-id={item.id}
          aria-pressed={isActive}
          aria-disabled={item.disabled ? "true" : "false"}
          tabIndex={isActive ? 0 : -1}
          onClick={handleButtonClick}
        >
          {item.text}
          {item.tagProps && (
            <Tag color="neutral" className="min-w-6" {...item.tagProps} />
          )}
        </button>
      );
    });
  }, [items, activeItem, handleButtonClick]);

  const rootClass = cn(
    "relative items-center bg-neutral-500/5 rounded-xl p-0.5 gap-0.5 h-10",
    variant === "fill" ? "flex w-full" : "inline-flex",
    className,
  );

  return (
    <div
      ref={containerRef}
      className={rootClass}
      role="group"
      aria-label="Segment control"
      onKeyDown={handleKeyDown}
    >
      <div
        ref={selectionRef}
        aria-hidden="true"
        className={cn(
          "absolute inset-y-0.5 rounded-[0.625rem] bg-white shadow-sm",
          "transition-all duration-300",
          "motion-reduce:transition-none",
          "will-change-[left,width]",
        )}
      />
      {itemElements}
    </div>
  );
};
